import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faMousePointer, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FieldCollection } from '@feathr/blackbox';
import { Pill } from '@feathr/components';

import SelectOption from './SelectOption';
import type { ISelectOptionProps } from './SelectOptions.types';
import type { IFieldOptionData } from './SelectOptions.utils';
import { getFieldIcon } from './SelectOptions.utils';

import * as styles from './SelectOptions.css';

function getCollectionPills(
  t: TFunction,
): Partial<Record<FieldCollection, { icon: IconDefinition; label: string }>> {
  return {
    [FieldCollection.Person]: { icon: faUser, label: t('Attribute') },
    [FieldCollection.Breadcrumb]: { icon: faMousePointer, label: t('Activity') },
  };
}

/** The data-type icon and label shared by both field option renderers. */
function FieldOptionContents({ data }: Readonly<{ data: IFieldOptionData }>): JSX.Element {
  const icon = getFieldIcon(data);

  return (
    <div className={styles.fieldOption}>
      <div className={styles.contentWrapper}>{icon && <FontAwesomeIcon icon={icon} />}</div>
      <div className={styles.fieldOptionCell}>
        <span>{data.name || data.label || data.u_key}</span>
      </div>
    </div>
  );
}

export function FieldOption<T extends IFieldOptionData>(
  props: Readonly<ISelectOptionProps<T>>,
): JSX.Element {
  return (
    <SelectOption {...props}>
      <FieldOptionContents data={props.data} />
    </SelectOption>
  );
}

export function CollectionFieldOption<T extends IFieldOptionData>(
  props: Readonly<ISelectOptionProps<T>>,
): JSX.Element {
  const { t } = useTranslation();
  const { data } = props;
  const pill = data.collection ? getCollectionPills(t)[data.collection] : undefined;

  return (
    <SelectOption {...props}>
      <div className={styles.collectionFieldOption}>
        <FieldOptionContents data={data} />
        {!!pill && (
          <div className={styles.fieldOptionCell}>
            <Pill prefix={<FontAwesomeIcon icon={pill.icon} />}>{pill.label}</Pill>
          </div>
        )}
      </div>
    </SelectOption>
  );
}
